import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/utils/cn'
import { focusRing, segmentedRest, segmentedSelected } from '@/components/ui/buttonStyles'
import { Spinner } from '@/components/ui/Spinner'
import { STATUS_FILLS } from '@/components/vocabulary/VocabularyRow'
import { simulateSrs } from '@/api/admin'
import { SrsSlider } from '../SrsSlider'
import { RATING_COLOR, RATING_LABEL } from '../ratingColors'
import { AnimatedNumber } from './AnimatedNumber'
import { LabInspector } from './LabInspector'
import { LabLegend } from './LabLegend'
import { LabTimeline } from './LabTimeline'
import { LabWordSearch } from './LabWordSearch'
import { ParameterDeck } from './ParameterDeck'
import { baselineNodes, buildNodes, relativeDay, spanLabel } from './labGeometry'
import { compare, deltaText } from './planSummary'
import type { LabPlan, LabStep, LabSubject, LabWord, SrsSettings } from '@/types/admin'

type SubjectKind = LabSubject['kind']

const SUBJECTS: { kind: SubjectKind; label: string }[] = [
  { kind: 'new', label: 'New word' },
  { kind: 'strength', label: 'Known word' },
  { kind: 'word', label: 'One of yours' },
]

const PRESETS: { label: string; ratings: number[] }[] = [
  { label: 'All Good', ratings: [] },
  { label: 'Slip now', ratings: [1] },
  { label: 'Slip on the 3rd', ratings: [3, 3, 1] },
  { label: 'Rocky start', ratings: [2, 1, 3, 2, 1, 3] },
  { label: 'Always Hard', ratings: Array(24).fill(2) },
  { label: 'Always Easy', ratings: Array(24).fill(4) },
]

const sameRatings = (a: number[], b: number[]) => a.length === b.length && a.every((r, i) => r === b[i])

function WordCard({ word }: { word: LabWord }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 rounded-md border border-border-divider bg-surface-bg/60 px-5 py-4">
      <div className="min-w-0">
        <p className="font-mono text-xs uppercase tracking-widest text-muted">{word.language_name}</p>
        <p className="mt-1 font-display text-3xl leading-tight text-heading">{word.lemma}</p>
        <p className="font-serif text-base text-muted">{word.gloss}</p>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-xs uppercase tracking-widest text-muted">
        <span className={cn('rounded-full px-2.5 py-0.5 normal-case tracking-normal text-status-text', STATUS_FILLS[word.status])}>
          {word.status}
        </span>
        <span>next in {spanLabel(word.interval_days)}</span>
        <span>ease {word.ease_factor.toFixed(2)}</span>
        <span>{word.reviews_count} reviews</span>
      </div>
    </div>
  )
}

function Readout({ plan }: { plan: LabPlan }) {
  const { candidate, reviewDelta, dayDelta } = compare(plan.steps, plan.baseline)
  const first = plan.steps[0]
  const same = reviewDelta === 0 && dayDelta === 0
  return (
    <section aria-label="Outcome" className="rounded-md border border-border-divider bg-surface-bg/40 p-5">
      <h3 className="font-serif text-lg text-heading">Outcome</h3>
      <dl className="mt-4 grid gap-px overflow-hidden rounded-md border border-border-divider bg-border-divider">
      <div className="bg-page-bg px-5 py-4">
        <dt className="font-mono text-xs uppercase tracking-widest text-muted">Mastered after</dt>
        <dd className="mt-1 font-display text-3xl text-heading">
          {candidate.reviews === null ? (
            <span className="font-serif text-base">not within 24 reviews</span>
          ) : (
            <>
              <AnimatedNumber value={candidate.reviews} />
              <span className="ml-2 font-serif text-base text-muted">
                reviews · <AnimatedNumber value={candidate.days ?? 0} /> days
              </span>
            </>
          )}
        </dd>
      </div>
      <div className="bg-page-bg px-5 py-4">
        <dt className="font-mono text-xs uppercase tracking-widest text-muted">Against saved settings</dt>
        {same ? (
          <dd className="mt-2 font-serif text-base text-heading">Same as saved settings</dd>
        ) : (
          <>
            <dd className="mt-2 font-serif text-base text-heading">{deltaText(reviewDelta, 'review')}</dd>
            <dd className="font-serif text-sm text-muted">{deltaText(dayDelta, 'day')}</dd>
          </>
        )}
      </div>
      <div className="bg-page-bg px-5 py-4">
        <dt className="font-mono text-xs uppercase tracking-widest text-muted">Next review</dt>
        <dd className="mt-2 font-serif text-base text-heading">{first ? relativeDay(first.day) : 'none'}</dd>
        {first && (
          <dd className="font-serif text-sm text-muted">
            rated {RATING_LABEL[first.rating]}, it earns {spanLabel(first.interval_days)}
          </dd>
        )}
      </div>
      </dl>
    </section>
  )
}

interface ScriptBarProps {
  steps: LabStep[]
  ratings: number[]
  activeKey: string | null
  onPreset: (ratings: number[]) => void
  onActivate: (key: string) => void
}

/** Presets that fill the script, and the planned ratings as a row of chips to jump between. */
function ScriptBar({ steps, ratings, activeKey, onPreset, onActivate }: ScriptBarProps) {
  return (
    <div className="space-y-3">
      <h3 className="font-serif text-lg text-heading">Script</h3>
      <div className="flex flex-wrap gap-2" aria-label="Script presets" role="group">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            aria-pressed={sameRatings(ratings, preset.ratings)}
            onClick={() => onPreset(preset.ratings)}
            className={cn(
              'min-h-11 rounded-full px-4 font-serif text-sm transition-colors duration-200',
              sameRatings(ratings, preset.ratings) ? segmentedSelected : segmentedRest,
              focusRing,
            )}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <ol aria-label="Planned ratings" className="flex flex-wrap gap-1.5">
        {steps.map((step, i) => (
          <li key={i}>
            <button
              type="button"
              onClick={() => onActivate(`s${i}`)}
              aria-label={`Review ${step.review}: ${RATING_LABEL[step.rating]}${step.scripted ? '' : ', assumed'}`}
              aria-current={activeKey === `s${i}` ? 'true' : undefined}
              className={cn(
                'flex h-8 min-w-8 items-center justify-center gap-1 rounded-full border px-2 font-mono text-xs text-body transition-colors duration-200',
                activeKey === `s${i}` ? 'border-em' : 'border-border-divider',
                !step.scripted && 'opacity-55',
                focusRing,
              )}
            >
              <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: RATING_COLOR[step.rating] }} />
              {step.review}
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}

interface SchedulingLabProps {
  draft: SrsSettings
  saved: SrsSettings
  onChange: (key: keyof SrsSettings, value: number) => void
  valid: boolean
}

/**
 * The scheduling lab: pick a word (new, known at some strength, or one of your
 * own), script how its future reviews go, and tune the parameters right under
 * the timeline — the plan the sliders produce is drawn against the plan the
 * saved settings produce.
 */
export function SchedulingLab({ draft, saved, onChange, valid }: SchedulingLabProps) {
  const [kind, setKind] = useState<SubjectKind>('new')
  const [fromDays, setFromDays] = useState(60)
  const [word, setWord] = useState<LabWord | null>(null)
  const [ratings, setRatings] = useState<number[]>([])
  const [plan, setPlan] = useState<LabPlan | null>(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeKey, setActiveKey] = useState<string | null>(null)

  const subject = useMemo<LabSubject | null>(() => {
    if (kind === 'strength') return { kind, from_days: fromDays }
    if (kind === 'word') return word ? { kind, user_word_sense_id: word.id } : null
    return { kind: 'new' }
  }, [kind, fromDays, word])

  // Debounced so a dragged slider settles before asking; the last plan stays on
  // screen meanwhile, so the next one morphs from it rather than from nothing.
  useEffect(() => {
    if (!subject || !valid) return
    setLoading(true)
    const timer = setTimeout(() => {
      simulateSrs(draft, subject, ratings)
        .then((res) => {
          setPlan(res.data.data)
          setError(false)
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false))
    }, 180)
    return () => clearTimeout(timer)
  }, [draft, subject, ratings, valid])

  const nodes = useMemo(() => (plan && subject ? buildNodes(plan, subject) : []), [plan, subject])
  const ghost = useMemo(() => (plan ? baselineNodes(plan) : []), [plan])
  const activeNode = nodes.find((node) => node.key === activeKey) ?? nodes.find((node) => node.kind === 'plan')
  const showPlan = plan !== null && subject !== null

  function choose(next: SubjectKind) {
    setKind(next)
    setRatings(next === 'strength' ? [1] : [])
    setActiveKey(null)
  }

  /** Scripts one review; reviews it skips over are assumed Good. */
  function rate(index: number, rating: number) {
    setRatings((current) => {
      const next = Array.from({ length: Math.max(current.length, index + 1) }, (_, i) => current[i] ?? 3)
      next[index] = rating
      return next
    })
  }

  return (
    <section aria-label="Scheduling lab" className="space-y-6">
      <p className="max-w-prose font-serif text-sm leading-relaxed text-muted">
        Pick a word and click its planned reviews to set how they go, then tune the parameters
        beneath the timeline.
      </p>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-end">
        <div role="radiogroup" aria-label="Word to simulate" className="grid grid-cols-3 gap-2">
          {SUBJECTS.map((option) => (
            <button
              key={option.kind}
              type="button"
              role="radio"
              aria-checked={kind === option.kind}
              onClick={() => choose(option.kind)}
              className={cn(
                'min-h-11 rounded-full px-3 font-mono text-xs uppercase tracking-widest transition-colors duration-200',
                kind === option.kind ? segmentedSelected : segmentedRest,
                focusRing,
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {kind === 'strength' && (
          <SrsSlider
            id="lab-from-days"
            label="Strength it has reached"
            hint="Preview only. The word was last sent this far ahead, so it falls due today."
            value={fromDays}
            min={7}
            max={365}
            step={1}
            suffix="d"
            compact
            onChange={setFromDays}
          />
        )}
        {kind === 'word' && (
          <LabWordSearch
            onPick={(picked) => {
              setWord(picked)
              setRatings([])
              setActiveKey(null)
            }}
          />
        )}
      </div>

      {kind === 'word' && word && <WordCard word={word} />}
      {kind === 'word' && word?.status === 'mastered' && (
        <p className="font-serif text-sm text-muted">
          This word is mastered and out of the queue. The plan shows what would happen if it came back.
        </p>
      )}

      {kind === 'word' && !word ? (
        <p className="rounded-md border border-dashed border-border-divider px-5 py-10 text-center font-serif text-base text-muted">
          Search for a word you track to see where it stands and where it is heading.
        </p>
      ) : error && !plan ? (
        <p className="font-serif text-base text-body">Could not run the simulation. Adjust a slider to retry.</p>
      ) : !plan ? (
        <p className="flex items-center gap-2 font-serif text-sm text-muted">
          <Spinner />
          Simulating reviews…
        </p>
      ) : (
        <div className={cn('space-y-6 transition-opacity duration-200', loading && 'opacity-80')}>
          <ScriptBar
            steps={plan.steps}
            ratings={ratings}
            activeKey={activeNode?.key ?? null}
            onPreset={(next) => {
              setRatings(next)
              setActiveKey(null)
            }}
            onActivate={setActiveKey}
          />
          <LabTimeline
            nodes={nodes}
            ghost={ghost}
            masteryThreshold={draft.mastery_threshold}
            knowWellInterval={draft.know_well_interval}
            activeKey={activeNode?.key ?? null}
            onActivate={setActiveKey}
            onRate={rate}
          />
          <LabLegend />
        </div>
      )}

      {/* The outcome and the inspected review on the left; the parameters beside them, under the curve they bend. */}
      <div className={cn('grid gap-6 lg:items-start', showPlan && 'lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]')}>
        {showPlan && plan && (
          <div className="space-y-5">
            <Readout plan={plan} />
            {activeNode && <LabInspector node={activeNode} plan={plan} onRate={rate} />}
          </div>
        )}
        <ParameterDeck
          draft={draft}
          saved={saved}
          onChange={onChange}
          hasSlip={plan?.steps.some((step) => step.rating === 1) ?? false}
          onTrySlip={() => choose('strength')}
        />
      </div>
    </section>
  )
}
